import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast } from "react-toastify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import MenuButton from "../MenuButton/MenuButton";
import UserServices from "../../UserServices/UserServices";
import { NoteState } from "../../context/notes/NoteState";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import { buildHrMonthStats } from "./hrDashboardStats";
import "../AdminDashboard/AdminDashboard.css";
import "./HRDashboard.css";

const PARTNER_DESIGNATION_IDS = [117];
const F11_BRANCH_ID = "15";
const G13_BRANCH_ID = "1";
const LATE_GRACE_MINUTES = 15;

const normalizeRows = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const isPartnerDesignation = (designationId) =>
  PARTNER_DESIGNATION_IDS.includes(Number(designationId));

const isF11Branch = (branchId) => String(branchId) === F11_BRANCH_ID;
const isG13Branch = (branchId) => String(branchId) === G13_BRANCH_ID;

const getEmployeeName = (employee = {}, fallback = "N/A") => {
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return fullName || fallback;
};

const formatDate = (value, fallback = "N/A") =>
  value && moment(value).isValid() ? moment(value).format("MMM DD, YYYY") : fallback;

const formatTime = (value, format = "HH:mm:ss") => {
  if (!value) return "N/A";
  const parsed = moment(value, format, true);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

const getBranchLabel = (branchId, branchName) => {
  if (branchName) return branchName;
  if (isF11Branch(branchId)) return "F-11";
  if (isG13Branch(branchId)) return "G-13";
  return "N/A";
};

const createDaySummary = (label, date) => ({
  label,
  date,
  present: 0,
  late: 0,
  absent: 0,
  onLeave: 0,
  f11: { present: 0, late: 0, absent: 0, onLeave: 0 },
  g13: { present: 0, late: 0, absent: 0, onLeave: 0 },
});

const parseAttendanceDutyWindow = (attendance) => {
  const savedDutyHours = attendance?.duty_hours;
  if (savedDutyHours && typeof savedDutyHours === "string" && savedDutyHours.includes("-")) {
    const [startTime, endTime] = savedDutyHours.split("-").map((value) => value.trim());
    return {
      start_time: startTime || null,
      end_time: endTime || null,
    };
  }

  return {
    start_time: attendance?.start_time || null,
    end_time: attendance?.end_time || null,
  };
};

const calculateLateDetails = (attendance) => {
  const dutyWindow = parseAttendanceDutyWindow(attendance);

  if (!dutyWindow.start_time || !attendance.checkin_time_24h) {
    return {
      isLate: false,
      lateMinutes: 0,
      lateText: "On Time",
      dutyStartTime: "N/A",
      dutyEndTime: "N/A",
    };
  }

  const dutyStart = moment(
    dutyWindow.start_time,
    ["HH:mm:ss", "HH:mm", "h:mm A"],
    true
  );
  const dutyEnd = dutyWindow.end_time
    ? moment(dutyWindow.end_time, ["HH:mm:ss", "HH:mm", "h:mm A"], true)
    : null;
  const checkIn = moment(attendance.checkin_time_24h, "HH:mm:ss", true);

  if (!dutyStart.isValid() || !checkIn.isValid()) {
    return {
      isLate: false,
      lateMinutes: 0,
      lateText: "On Time",
      dutyStartTime: formatTime(dutyWindow.start_time, "HH:mm:ss"),
      dutyEndTime: formatTime(dutyWindow.end_time, "HH:mm:ss"),
    };
  }

  const allowedCheckIn = dutyStart.clone().add(LATE_GRACE_MINUTES, "minutes");
  const lateMinutes = checkIn.isAfter(dutyStart)
    ? checkIn.diff(dutyStart, "minutes")
    : 0;
  const isLate =
    attendance?.is_late !== undefined && attendance?.is_late !== null
      ? Number(attendance.is_late) === 1
      : checkIn.isAfter(allowedCheckIn);

  return {
    isLate,
    lateMinutes,
    lateText:
      isLate
        ? `${Math.floor(lateMinutes / 60) > 0 ? `${Math.floor(lateMinutes / 60)}h ` : ""}${
            lateMinutes % 60
          }m`.trim()
        : "On Time",
    dutyStartTime: dutyStart.format("hh:mm A"),
    dutyEndTime: dutyEnd?.isValid() ? dutyEnd.format("hh:mm A") : "N/A",
  };
};

const summarizeDepartments = (staffRows) => {
  const departmentMap = {};

  staffRows.forEach((staff) => {
    const departmentName = staff.department || "Unassigned";
    const designationName = staff.designation || "Unassigned";

    if (!departmentMap[departmentName]) {
      departmentMap[departmentName] = {
        id: departmentName,
        department: departmentName,
        total: 0,
        f11: 0,
        g13: 0,
        designations: {},
      };
    }

    const entry = departmentMap[departmentName];
    entry.total += 1;
    entry.designations[designationName] = (entry.designations[designationName] || 0) + 1;
    if (isF11Branch(staff.branch_id)) entry.f11 += 1;
    if (isG13Branch(staff.branch_id)) entry.g13 += 1;
  });

  return Object.values(departmentMap)
    .map((department) => ({
      ...department,
      designationSummary: Object.entries(department.designations)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([name, count]) => `${name} (${count})`)
        .join(", "),
      topDesignations: Object.entries(department.designations)
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .slice(0, 2),
    }))
    .sort((a, b) => b.total - a.total || a.department.localeCompare(b.department));
};

const rebuildDaySummaries = (summaries, presentRows, lateRows, absentRows, leaveRows) =>
  summaries.map((summary) => {
    const next = createDaySummary(summary.label, summary.date);
    const add = (row, key) => {
      if (row.date !== summary.date) return;
      next[key] += 1;
      const branchKey = isF11Branch(row.branch_id)
        ? "f11"
        : isG13Branch(row.branch_id)
        ? "g13"
        : null;
      if (branchKey) next[branchKey][key] += 1;
    };

    presentRows.forEach((row) => add(row, "present"));
    lateRows.forEach((row) => add(row, "late"));
    absentRows.forEach((row) => add(row, "absent"));
    leaveRows.forEach((row) => add(row, "onLeave"));
    return next;
  });

const sortRowsByDateAndName = (rows) =>
  [...rows].sort((a, b) => {
    const dateCompare = moment(a.date).diff(moment(b.date));
    if (dateCompare !== 0) return dateCompare;
    return (a.name || "").localeCompare(b.name || "");
  });

const SummaryCard = ({ title, value, tone, children, onClick, columns = "col-12 col-md-6 col-xl-4" }) => (
  <div className={`${columns} mb-3`}>
    <div
      className={`card radius-10 custom-shadow h-100 border-${tone} ${
        onClick ? "hr-dashboard-clickable" : ""
      }`}
      onClick={onClick}
      onKeyDown={(event) => {
        if (onClick && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick();
        }
      }}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="card-body">
        <p className="text-muted mb-2">{title}</p>
        <h2 className={`mb-3 fw-bold text-${tone}`}>{value}</h2>
        {children}
      </div>
    </div>
  </div>
);

const DetailAccordionItem = ({ id, title, tone, count, children }) => (
  <div className="accordion-item">
    <h2 className="accordion-header" id={`${id}-heading`}>
      <button
        className="accordion-button collapsed d-flex justify-content-between align-items-center gap-3"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target={`#${id}-collapse`}
        aria-expanded="false"
        aria-controls={`${id}-collapse`}
      >
        <span className={`fw-semibold text-${tone}`}>{title}</span>
        <span className={`badge bg-${tone}`}>{count}</span>
      </button>
    </h2>
    <div
      id={`${id}-collapse`}
      className="accordion-collapse collapse"
      aria-labelledby={`${id}-heading`}
    >
      <div className="accordion-body">{children}</div>
    </div>
  </div>
);

const DataTable = ({ rows, columns, emptyMessage }) => {
  if (!rows.length) {
    return <p className="text-muted text-center py-3 mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-hover table-striped align-middle mb-0">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.label}>{column.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id || row.uid || `${row.name || "row"}-${index}`}>
              {columns.map((column) => (
                <td key={`${column.label}-${index}`}>
                  {column.render ? column.render(row, index) : row[column.key] || "N/A"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const rowSearchText = (row) =>
  [
    row?.name,
    row?.uid,
    row?.first_name,
    row?.last_name,
    row?.department,
    row?.department_name,
    row?.designation,
    row?.designation_name,
    row?.branch,
    row?.branch_name,
    row?.phone,
    row?.user_info?.name,
    row?.employee?.name,
    row?.employee?.uid,
    row?.leave_type,
    row?.reason,
    row?.subject,
    row?.document_category,
  ]
    .filter((value) => value != null && value !== "")
    .join(" ")
    .toLowerCase();

const DashboardModal = ({ config, onClose }) => {
  const [query, setQuery] = useState("");

  useEffect(() => {
    setQuery("");
  }, [config?.title]);

  if (!config) return null;

  const needle = query.trim().toLowerCase();
  const sections = config.sections.map((section) => {
    if (!needle) return section;
    return {
      ...section,
      rows: section.rows.filter((row) => rowSearchText(row).includes(needle)),
    };
  });

  return (
    <div className="hr-dashboard-modal-backdrop" onClick={onClose}>
      <div
        className="hr-dashboard-modal-card"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="hr-dashboard-modal-header">
          <div>
            <h4 className="mb-1">{config.title}</h4>
            {config.subtitle ? (
              <p className="text-muted mb-0">{config.subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onClose}
          >
            Close
          </button>
        </div>

        <div className="hr-dashboard-modal-toolbar">
          {config.summaryBadges?.length
            ? config.summaryBadges.map((badge) => (
                <span key={`${config.title}-${badge.label}`} className="badge text-bg-light border">
                  {badge.label}: {badge.value}
                </span>
              ))
            : null}
          <input
            type="search"
            className="form-control hr-dashboard-modal-search"
            placeholder="Search name, ID, or department"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search this list"
          />
        </div>

        <div className="hr-dashboard-modal-body">
          {sections.map((section) => (
            <div key={`${config.title}-${section.title}`} className="hr-dashboard-modal-section">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h6 className={`mb-0 text-${section.tone || "primary"}`}>{section.title}</h6>
                <span className={`badge bg-${section.tone || "primary"}`}>
                  {section.rows.length}
                </span>
              </div>
              <DataTable
                rows={section.rows}
                columns={section.columns}
                emptyMessage={
                  needle ? "No matching records in this list." : section.emptyMessage
                }
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const ApprovalActions = ({
  note,
  onNoteChange,
  onApprove,
  onReject,
  approveLoading,
  rejectLoading,
}) => (
  <div className="hr-approval-actions">
    <textarea
      className="form-control hr-review-note"
      rows="3"
      placeholder="Optional HR note"
      value={note}
      onChange={onNoteChange}
    />
    <div className="hr-approval-button-row">
      <button
        type="button"
        className="btn btn-sm btn-success"
        disabled={approveLoading || rejectLoading}
        onClick={onApprove}
      >
        {approveLoading ? "Saving..." : "Approve"}
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        disabled={approveLoading || rejectLoading}
        onClick={onReject}
      >
        {rejectLoading ? "Saving..." : "Reject"}
      </button>
    </div>
  </div>
);

const loadAttendanceRange = async (activeBranch, sdate, edate) => {
  const rows = [];
  let page = 1;
  let totalPages = 1;

  while (page <= totalPages && page <= 15) {
    try {
      const response = await UserServices.getEmployeeAttendence(
        activeBranch,
        { sdate, edate, start_time: null, end_time: null, gender: null },
        null,
        "500",
        String(page)
      );
      if (response.status !== 200) break;
      const batch = normalizeRows(response);
      rows.push(...batch);
      const pages = Number(response?.data?.totalPages || response?.data?.data?.last_page || 1);
      totalPages = Number.isFinite(pages) && pages > 0 ? pages : 1;
      if (!batch.length) break;
      page += 1;
    } catch (error) {
      break;
    }
  }

  return rows;
};

const Kpi = ({ label, value, sub, tone = "slate", icon, onClick }) => (
  <div
    className={`ad-kpi ad-tone-${tone} ${onClick ? "hr-kpi-button" : ""}`}
    onClick={onClick}
    onKeyDown={(event) => {
      if (onClick && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onClick();
      }
    }}
    role={onClick ? "button" : undefined}
    tabIndex={onClick ? 0 : undefined}
  >
    <div className="ad-kpi-body">
      <p className="ad-kpi-label">{label}</p>
      <h4 className="ad-kpi-value">{value}</h4>
      {sub ? <p className="ad-kpi-sub">{sub}</p> : null}
    </div>
    <div className="ad-kpi-icon">
      <i className={`bi ${icon}`}></i>
    </div>
  </div>
);

const RankTable = ({ rows, valueLabel, valueKey, emptyMessage }) => {
  if (!rows.length) {
    return <p className="ad-empty">{emptyMessage}</p>;
  }

  return (
    <div className="ad-table-wrap">
      <table className="ad-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Employee</th>
            <th>Department</th>
            <th>{valueLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 8).map((row, index) => (
            <tr key={row.id || `${row.name}-${index}`}>
              <td>{index + 1}</td>
              <td>
                <strong>{row.name}</strong>
                <div className="hr-rank-meta">{row.branch}</div>
              </td>
              <td>{row.department}</td>
              <td>{row[valueKey]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const HRDashboard = () => {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const { branch: selectedBranch, setBranch: setSelectedBranch } =
    useDashboardBranch(role, branch_id);
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  const selectedDateKey = moment(selectedDate).format("YYYY-MM-DD");
  const monthStart = moment(selectedDateKey).startOf("month").format("YYYY-MM-DD");
  const isSelectedToday = selectedDateKey === moment().format("YYYY-MM-DD");
  const dayLabel = isSelectedToday ? "Today" : formatDate(selectedDateKey);
  const monthLabel = `${moment(monthStart).format("MMM D")} – ${moment(selectedDateKey).format("MMM D, YYYY")}`;

  const [staffSummary, setStaffSummary] = useState({
    total: 0,
    f11: 0,
    g13: 0,
  });
  const [departmentSummary, setDepartmentSummary] = useState([]);
  const [attendanceSummary, setAttendanceSummary] = useState({
    selected: createDaySummary("Today", moment().format("YYYY-MM-DD")),
  });
  const [monthRecords, setMonthRecords] = useState([]);
  const [staffDetails, setStaffDetails] = useState([]);
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [onLeaveEmployees, setOnLeaveEmployees] = useState([]);
  const [birthdaysThisMonth, setBirthdaysThisMonth] = useState([]);
  const [anniversariesThisMonth, setAnniversariesThisMonth] = useState([]);
  const [pendingDutyRequests, setPendingDutyRequests] = useState([]);
  const [pendingDocumentReviews, setPendingDocumentReviews] = useState([]);
  const [pendingLeaveApplications, setPendingLeaveApplications] = useState([]);
  const [approvedLeaveApplications, setApprovedLeaveApplications] = useState([]);
  const [reviewNotes, setReviewNotes] = useState({});
  const [actionKey, setActionKey] = useState("");
  const [activeView, setActiveView] = useState(null);

  const handleReview = async (type, id, approvalStatus, row = null) => {
    const noteKey = `${type}-${id}`;
    const requestKey = `review-${noteKey}-${approvalStatus}`;
    setActionKey(requestKey);

    try {
      if (type === "leave") {
        const today = moment().format("YYYY-MM-DD");
        if (approvalStatus === "Approved") {
          if (row?.leave_status === "Leave") {
            await UserServices.availabilityLeaveCheckForApproval({
              number_of_leaves: row.number_of_leaves,
              leave_type: row.leave_type,
              user_id: row.user_id || row.user_info?.id,
            });
          }
          await UserServices.approveRequest(id, today);
        } else {
          await UserServices.rejectRequest(id, today);
        }
        toast.success(`Leave application ${approvalStatus.toLowerCase()} successfully.`);
      } else {
        const payload = {
          approval_status: approvalStatus,
          review_notes: reviewNotes[noteKey] || "",
        };

        if (type === "duty") {
          await UserServices.reviewEmployeeDutyHourRequest(id, payload);
        } else {
          await UserServices.reviewEmployeeDocument(id, payload);
        }

        toast.success(
          `${type === "duty" ? "Duty-hour request" : "Document"} ${approvalStatus.toLowerCase()} successfully.`
        );
      }

      setReviewNotes((previous) => ({
        ...previous,
        [noteKey]: "",
      }));
      await fetchData();
    } catch (error) {
      toast.error(
        error?.response?.data?.messages ||
          error?.response?.data?.message ||
          "Unable to update approval status."
      );
    } finally {
      setActionKey("");
    }
  };

  const fetchBranches = async () => {
    try {
      const branchResponse = await UserServices.branchName();
      if (branchResponse.status === 200) {
        setBranches(branchResponse.data.data || []);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);

    try {
      const activeBranch = selectedBranch === "all" ? "" : selectedBranch || branch_id || "";

      const [staffResult, leaveResult, dutyApprovalResult, documentApprovalResult] =
        await Promise.allSettled([
          UserServices.getactiveUsers(activeBranch, "1", "1000", "1", "", "", "staff"),
          UserServices.getLeaveApplications(activeBranch, "1000", "1"),
          UserServices.getEmployeeDutyHourRequests({
            branch_id: activeBranch,
            approval_status: "Pending",
            limit: 1000,
          }),
          UserServices.getEmployeeDocuments({
            branch_id: activeBranch,
            approval_status: "Pending",
            limit: 1000,
          }),
        ]);

      if (staffResult.status !== "fulfilled" || staffResult.value.status !== 200) {
        throw new Error("Failed to load staff records");
      }

      const allStaff = normalizeRows(staffResult.value);
      const leaveRows =
        leaveResult.status === "fulfilled" && leaveResult.value.status === 200
          ? normalizeRows(leaveResult.value)
          : [];
      const pendingDutyRows =
        dutyApprovalResult.status === "fulfilled" && dutyApprovalResult.value.status === 200
          ? normalizeRows(dutyApprovalResult.value)
          : [];
      const pendingDocumentRows =
        documentApprovalResult.status === "fulfilled" && documentApprovalResult.value.status === 200
          ? normalizeRows(documentApprovalResult.value)
          : [];

      setPendingDutyRequests(pendingDutyRows);
      setPendingDocumentReviews(pendingDocumentRows);
      setPendingLeaveApplications(
        leaveRows.filter((leave) => leave.application_status === "Pending")
      );

      const attendanceResult = await Promise.allSettled([
        loadAttendanceRange(activeBranch, monthStart, selectedDateKey),
      ]).then(([result]) => result);
      const attendanceRows =
        attendanceResult.status === "fulfilled" && Array.isArray(attendanceResult.value)
          ? attendanceResult.value
          : [];

      const scopedStaff = allStaff.filter(
        (staff) =>
          Number(staff.is_login_account) !== 1 &&
          !isPartnerDesignation(staff.designation_id)
      );
      const f11Staff = scopedStaff.filter((staff) => isF11Branch(staff.branch_id));
      const g13Staff = scopedStaff.filter((staff) => isG13Branch(staff.branch_id));

      const staffLookup = {};
      scopedStaff.forEach((staff) => {
        staffLookup[String(staff.id)] = staff;
      });

      setStaffSummary({
        total: scopedStaff.length,
        f11: f11Staff.length,
        g13: g13Staff.length,
      });

      const departmentMap = {};
      scopedStaff.forEach((staff) => {
        const departmentName = staff.department_name || staff.department || "Unassigned";
        const designationName = staff.designation_name || staff.designation || "Unassigned";

        if (!departmentMap[departmentName]) {
          departmentMap[departmentName] = {
            id: departmentName,
            department: departmentName,
            total: 0,
            f11: 0,
            g13: 0,
            designations: {},
          };
        }

        departmentMap[departmentName].total += 1;
        departmentMap[departmentName].designations[designationName] =
          (departmentMap[departmentName].designations[designationName] || 0) + 1;

        if (isF11Branch(staff.branch_id)) departmentMap[departmentName].f11 += 1;
        if (isG13Branch(staff.branch_id)) departmentMap[departmentName].g13 += 1;
      });

      const departmentRows = Object.values(departmentMap)
        .map((department) => ({
          ...department,
          designationSummary: Object.entries(department.designations)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .map(([name, count]) => `${name} (${count})`)
            .join(", "),
          topDesignations: Object.entries(department.designations)
            .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
            .slice(0, 2),
        }))
        .sort((a, b) => b.total - a.total || a.department.localeCompare(b.department));

      setDepartmentSummary(departmentRows);

      const staffDirectoryRows = [...scopedStaff]
        .map((staff) => ({
          id: staff.id,
          uid: staff.uid || "N/A",
          name: getEmployeeName(staff),
          branch_id: staff.branch_id,
          branch: getBranchLabel(staff.branch_id, staff.branch_name),
          department: staff.department_name || staff.department || "N/A",
          designation: staff.designation_name || staff.designation || "N/A",
          joining: staff.joining,
          phone: staff.phone || "N/A",
        }))
        .sort(
          (a, b) =>
            a.department.localeCompare(b.department) || a.name.localeCompare(b.name)
        );

      setStaffDetails(staffDirectoryRows);

      const daySummaryMap = {
        [selectedDateKey]: createDaySummary(dayLabel, selectedDateKey),
      };

      const presentRows = [];
      const lateRows = [];
      const absentRows = [];
      const monthRows = [];

      attendanceRows.forEach((attendance) => {
        const staffId = String(attendance.attendee_id);
        const attendanceDate = String(attendance.date || "").slice(0, 10);

        if (!staffLookup[staffId]) return;
        if (!attendanceDate || attendanceDate < monthStart || attendanceDate > selectedDateKey) return;

        const staff = staffLookup[staffId] || {};
        const branchId = staff.branch_id || attendance.branch_id;
        const branchKey = isF11Branch(branchId) ? "f11" : isG13Branch(branchId) ? "g13" : null;
        const lateDetails = calculateLateDetails(attendance);
        const isPresent = attendance.attendance_status === "Present";
        const isAbsent = attendance.attendance_status === "Absent";

        const baseRow = {
          id: `${attendance.id || staffId}-${attendanceDate}`,
          name: getEmployeeName(attendance.attendee || staff, attendance.user_name || "N/A"),
          date: attendanceDate,
          branch_id: branchId,
          branch: getBranchLabel(branchId, staff.branch_name),
          designation:
            attendance.designation ||
            staff.designation_name ||
            staff.designation ||
            "N/A",
          department: staff.department_name || staff.department || "N/A",
          duty_start_time: lateDetails.dutyStartTime,
          duty_end_time: lateDetails.dutyEndTime,
          checkin_time: formatTime(
            attendance.checkin_time_24h || attendance.checkin_time_12h,
            attendance.checkin_time_24h ? "HH:mm:ss" : "hh:mm A"
          ),
          checkout_time: formatTime(
            attendance.checkout_time_24h || attendance.checkout_time_12h,
            attendance.checkout_time_24h ? "HH:mm:ss" : "hh:mm A"
          ),
          working_hours: attendance.working_hours || "N/A",
        };

        monthRows.push({
          ...baseRow,
          staffId,
          isPresent,
          isLate: Boolean(isPresent && lateDetails.isLate),
          isAbsent,
          lateMinutes: lateDetails.lateMinutes,
          lateText: lateDetails.lateText,
        });

        if (attendanceDate !== selectedDateKey) return;

        if (isPresent) {
          daySummaryMap[attendanceDate].present += 1;
          if (branchKey) daySummaryMap[attendanceDate][branchKey].present += 1;

          presentRows.push({
            ...baseRow,
            status: lateDetails.isLate ? "Late" : "Present",
          });

          if (lateDetails.isLate) {
            daySummaryMap[attendanceDate].late += 1;
            if (branchKey) daySummaryMap[attendanceDate][branchKey].late += 1;

            lateRows.push({
              ...baseRow,
              late_minutes: lateDetails.lateMinutes,
              late_time: lateDetails.lateText,
            });
          }
        }

        if (isAbsent) {
          daySummaryMap[attendanceDate].absent += 1;
          if (branchKey) daySummaryMap[attendanceDate][branchKey].absent += 1;

          absentRows.push({
            ...baseRow,
            reason: attendance.reason || "N/A",
            remarks: attendance.remarks || "N/A",
          });
        }
      });

      const pendingLeaves = leaveRows.filter(
        (leave) => leave.application_status === "Pending"
      );
      const approvedLeaves = leaveRows.filter(
        (leave) => leave.application_status === "Approved"
      );

      const onLeaveRows = [];
      approvedLeaves.forEach((leave, index) => {
        const userId = String(
          leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id || ""
        );
        const leaveFrom = leave.from || leave.leave_from_date;
        const leaveTo = leave.to || leave.leave_to_date;

        if (!userId || !leaveFrom || !leaveTo) return;

        const staff = staffLookup[userId] || {};
        const branchId = leave.branch_id || staff.branch_id;
        const branchKey = isF11Branch(branchId) ? "f11" : isG13Branch(branchId) ? "g13" : null;

        [selectedDateKey].forEach((dateKey) => {
          if (
            moment(dateKey).isBetween(
              leaveFrom,
              leaveTo,
              undefined,
              "[]"
            )
          ) {
            daySummaryMap[dateKey].onLeave += 1;
            if (branchKey) daySummaryMap[dateKey][branchKey].onLeave += 1;

            onLeaveRows.push({
              id: `${leave.id || index}-${userId}-${dateKey}`,
              name:
                leave.user_info?.name ||
                getEmployeeName(staff, getEmployeeName(leave.user_info)),
              date: dateKey,
              leave_from_date: leaveFrom,
              leave_to_date: leaveTo,
              leave_type: leave.leave_type || "N/A",
              reason: leave.reason || leave.remarks || "N/A",
              branch:
                leave.branch_info?.name ||
                getBranchLabel(branchId, staff.branch_name),
              designation: staff.designation_name || staff.designation || "N/A",
              department: staff.department_name || staff.department || "N/A",
            });
          }
        });
      });

      setAttendanceSummary({
        selected: daySummaryMap[selectedDateKey],
      });
      setMonthRecords(monthRows);
      setPresentEmployees(sortRowsByDateAndName(presentRows));
      setLateEmployees(
        [...lateRows].sort((a, b) => {
          const dateCompare = moment(a.date).diff(moment(b.date));
          if (dateCompare !== 0) return dateCompare;
          return b.late_minutes - a.late_minutes;
        })
      );
      setAbsentEmployees(sortRowsByDateAndName(absentRows));
      setOnLeaveEmployees(sortRowsByDateAndName(onLeaveRows));

      const currentMonth = moment().month();
      const today = moment();

      const birthdayRows = scopedStaff
        .filter((staff) => {
          if (!staff.dob) return false;
          const dob = moment(staff.dob);
          return dob.isValid() && dob.month() === currentMonth;
        })
        .map((staff) => {
          const dob = moment(staff.dob);
          return {
            ...staff,
            birthday_date: dob.format("MMM DD"),
            age: today.diff(dob, "years"),
          };
        })
        .sort((a, b) => moment(a.dob).date() - moment(b.dob).date());

      const anniversaryRows = scopedStaff
        .filter((staff) => {
          if (!staff.joining) return false;
          const joiningDate = moment(staff.joining);
          if (!joiningDate.isValid()) return false;

          const anniversaryThisYear = joiningDate.clone().year(today.year());
          return (
            anniversaryThisYear.month() === currentMonth &&
            anniversaryThisYear.isSameOrAfter(today, "day")
          );
        })
        .map((staff) => {
          const joiningDate = moment(staff.joining);
          const anniversaryThisYear = joiningDate.clone().year(today.year());
          return {
            ...staff,
            anniversary_date: anniversaryThisYear.format("MMM DD, YYYY"),
            years_of_service: today.diff(joiningDate, "years"),
          };
        })
        .sort((a, b) => moment(a.anniversary_date).diff(moment(b.anniversary_date)));

      setBirthdaysThisMonth(birthdayRows);
      setAnniversariesThisMonth(anniversaryRows);
      setPendingDutyRequests(pendingDutyRows);
      setPendingDocumentReviews(pendingDocumentRows);
      setPendingLeaveApplications(pendingLeaves);
      setApprovedLeaveApplications(approvedLeaves);
    } catch (error) {
      console.error("Error fetching HR dashboard data:", error);
      setStaffSummary({ total: 0, f11: 0, g13: 0 });
      setDepartmentSummary([]);
      setAttendanceSummary({
        selected: createDaySummary(dayLabel, selectedDateKey),
      });
      setMonthRecords([]);
      setStaffDetails([]);
      setPresentEmployees([]);
      setLateEmployees([]);
      setAbsentEmployees([]);
      setOnLeaveEmployees([]);
      setBirthdaysThisMonth([]);
      setAnniversariesThisMonth([]);
      setPendingDutyRequests([]);
      setPendingDocumentReviews([]);
      setPendingLeaveApplications([]);
      setApprovedLeaveApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  useEffect(() => {
    if (!activeView) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveView(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeView]);

  useEffect(() => {
    fetchData();
  }, [selectedBranch, selectedDate, branch_id]);

  const selectedBranchLabel =
    selectedBranch === "all"
      ? "All Branches"
      : branches.find((branch) => String(branch.id) === String(selectedBranch))?.name || "Branch";

  const daySummaries = [attendanceSummary.selected].filter(Boolean);

  const staffColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", key: "name" },
    { label: "Employee ID", key: "uid" },
    { label: "Branch", key: "branch" },
    { label: "Department", key: "department" },
    { label: "Designation", key: "designation" },
    {
      label: "Joining Date",
      render: (row) => formatDate(row.joining),
    },
    { label: "Phone", key: "phone" },
  ];

  const presentColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", key: "name" },
    { label: "Date", render: (row) => formatDate(row.date) },
    {
      label: "Duty Hours",
      render: (row) =>
        row.duty_start_time !== "N/A"
          ? `${row.duty_start_time} - ${row.duty_end_time}`
          : "N/A",
    },
    { label: "Check In", key: "checkin_time" },
    { label: "Check Out", key: "checkout_time" },
    { label: "Working Hours", key: "working_hours" },
    { label: "Branch", key: "branch" },
    { label: "Designation", key: "designation" },
    { label: "Department", key: "department" },
    {
      label: "Status",
      render: (row) => (
        <span className={`badge bg-${row.status === "Late" ? "warning" : "success"}`}>
          {row.status}
        </span>
      ),
    },
  ];

  const lateColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", key: "name" },
    { label: "Date", render: (row) => formatDate(row.date) },
    {
      label: "Duty Hours",
      render: (row) =>
        row.duty_start_time !== "N/A"
          ? `${row.duty_start_time} - ${row.duty_end_time}`
          : "N/A",
    },
    { label: "Check In", key: "checkin_time" },
    {
      label: "Late By",
      render: (row) => <span className="badge bg-warning">{row.late_time}</span>,
    },
    { label: "Branch", key: "branch" },
    { label: "Designation", key: "designation" },
    { label: "Department", key: "department" },
  ];

  const absentColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", key: "name" },
    { label: "Date", render: (row) => formatDate(row.date) },
    { label: "Reason", key: "reason" },
    { label: "Remarks", key: "remarks" },
    { label: "Branch", key: "branch" },
    { label: "Designation", key: "designation" },
    { label: "Department", key: "department" },
  ];

  const leaveColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", key: "name" },
    { label: "Date", render: (row) => formatDate(row.date) },
    {
      label: "Leave From",
      render: (row) => formatDate(row.leave_from_date),
    },
    {
      label: "Leave To",
      render: (row) => formatDate(row.leave_to_date),
    },
    { label: "Leave Type", key: "leave_type" },
    { label: "Reason", key: "reason" },
    { label: "Branch", key: "branch" },
    { label: "Designation", key: "designation" },
    { label: "Department", key: "department" },
  ];

  const nameNeedle = "";
  const matchesQuery = (row) => !nameNeedle || rowSearchText(row).includes(nameNeedle);
  const visibleStaffDetails = nameNeedle ? staffDetails.filter(matchesQuery) : staffDetails;
  const visiblePresent = nameNeedle ? presentEmployees.filter(matchesQuery) : presentEmployees;
  const visibleLate = nameNeedle ? lateEmployees.filter(matchesQuery) : lateEmployees;
  const visibleAbsent = nameNeedle ? absentEmployees.filter(matchesQuery) : absentEmployees;
  const visibleLeave = nameNeedle ? onLeaveEmployees.filter(matchesQuery) : onLeaveEmployees;
  const visibleBirthdays = nameNeedle ? birthdaysThisMonth.filter(matchesQuery) : birthdaysThisMonth;
  const visibleAnniversaries = nameNeedle
    ? anniversariesThisMonth.filter(matchesQuery)
    : anniversariesThisMonth;
  const visiblePendingLeaves = nameNeedle
    ? pendingLeaveApplications.filter(matchesQuery)
    : pendingLeaveApplications;
  const visibleApprovedLeaves = nameNeedle
    ? approvedLeaveApplications.filter(matchesQuery)
    : approvedLeaveApplications;
  const visibleDuty = nameNeedle ? pendingDutyRequests.filter(matchesQuery) : pendingDutyRequests;
  const visibleDocuments = nameNeedle
    ? pendingDocumentReviews.filter(matchesQuery)
    : pendingDocumentReviews;
  const visibleStaffSummary = nameNeedle
    ? {
        total: visibleStaffDetails.length,
        f11: visibleStaffDetails.filter((row) => isF11Branch(row.branch_id)).length,
        g13: visibleStaffDetails.filter((row) => isG13Branch(row.branch_id)).length,
      }
    : staffSummary;
  const visibleDepartmentSummary = nameNeedle
    ? summarizeDepartments(visibleStaffDetails)
    : departmentSummary;
  const visibleDaySummaries = nameNeedle
    ? rebuildDaySummaries(daySummaries, visiblePresent, visibleLate, visibleAbsent, visibleLeave)
    : daySummaries;
  const visibleMonthRecords = nameNeedle ? monthRecords.filter(matchesQuery) : monthRecords;
  const staffIds = new Set(visibleStaffDetails.map((row) => String(row.id)));
  const scopedApprovedLeaves = visibleApprovedLeaves.filter((leave) => {
    const userId = String(
      leave.user_id || leave.attendee_id || leave.member_id || leave.user_info?.id || ""
    );
    return staffIds.has(userId);
  });
  const monthStats = buildHrMonthStats({
    records: visibleMonthRecords,
    leaves: scopedApprovedLeaves,
    monthStart,
    monthEnd: selectedDateKey,
  });
  const todayOnTime = visiblePresent.filter((row) => row.status === "Present");
  const showBranchSplit = selectedBranch === "all";

  const renderApprovalActions = (type, row) => {
    const noteKey = `${type}-${row.id}`;
    return (
      <ApprovalActions
        note={reviewNotes[noteKey] || ""}
        onNoteChange={(event) =>
          setReviewNotes((previous) => ({
            ...previous,
            [noteKey]: event.target.value,
          }))
        }
        onApprove={() => handleReview(type, row.id, "Approved", row)}
        onReject={() => handleReview(type, row.id, "Rejected", row)}
        approveLoading={actionKey === `review-${noteKey}-Approved`}
        rejectLoading={actionKey === `review-${noteKey}-Rejected`}
      />
    );
  };

  const leaveApprovalColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", render: (row) => row.user_info?.name || "N/A" },
    {
      label: "Branch",
      render: (row) => row.branch_info?.name || getBranchLabel(row.branch_id),
    },
    { label: "Type", render: (row) => row.leave_status || "N/A" },
    { label: "Leave Type", render: (row) => row.leave_type || "N/A" },
    { label: "From", render: (row) => formatDate(row.from || row.leave_from_date) },
    { label: "To", render: (row) => formatDate(row.to || row.leave_to_date) },
    { label: "Days", render: (row) => row.number_of_leaves || "N/A" },
    { label: "Reason", render: (row) => row.reason || "N/A" },
    { label: "Actions", render: (row) => renderApprovalActions("leave", row) },
  ];

  const approvedLeaveColumns = leaveApprovalColumns.filter((column) => column.label !== "Actions");

  const dutyApprovalColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", render: (row) => row.employee?.name || "N/A" },
    { label: "Employee ID", render: (row) => row.employee?.uid || "N/A" },
    {
      label: "Branch",
      render: (row) => row.branch?.name || getBranchLabel(row.branch_id, row.branch_name),
    },
    { label: "Day", key: "day" },
    {
      label: "Current Hours",
      render: (row) => `${row.current_start_time || "--"} - ${row.current_end_time || "--"}`,
    },
    {
      label: "Requested Hours",
      render: (row) => `${row.requested_start_time || "--"} - ${row.requested_end_time || "--"}`,
    },
    { label: "Reason", render: (row) => row.reason || "N/A" },
    { label: "Actions", render: (row) => renderApprovalActions("duty", row) },
  ];

  const documentApprovalColumns = [
    { label: "S.No", render: (_, index) => index + 1 },
    { label: "Employee", render: (row) => row.employee?.name || "N/A" },
    { label: "Employee ID", render: (row) => row.employee?.uid || "N/A" },
    {
      label: "Branch",
      render: (row) => row.branch || row.branch?.name || getBranchLabel(row.branch_id),
    },
    { label: "Document", render: (row) => row.subject || row.document_category || "Document" },
    { label: "Category", key: "document_category" },
    { label: "Issue Date", render: (row) => formatDate(row.issue_date) },
    {
      label: "File",
      render: (row) =>
        row.file_url ? (
          <a href={row.file_url} target="_blank" rel="noreferrer">
            View File
          </a>
        ) : (
          "N/A"
        ),
    },
    { label: "Description", render: (row) => row.description || "N/A" },
    { label: "Actions", render: (row) => renderApprovalActions("document", row) },
  ];

  const modalConfig = (() => {
    if (!activeView) return null;

    if (activeView.kind === "staff") {
      const scope = activeView.scope;
      const scopedRows =
        scope === "f11"
          ? visibleStaffDetails.filter((row) => isF11Branch(row.branch_id))
          : scope === "g13"
          ? visibleStaffDetails.filter((row) => isG13Branch(row.branch_id))
          : visibleStaffDetails;
      const titleMap = {
        all: "Total Staff",
        f11: "F-11 Staff",
        g13: "G-13 Staff",
      };

      return {
        title: titleMap[scope] || "Staff",
        subtitle: selectedBranchLabel,
        summaryBadges: [{ label: "Records", value: scopedRows.length }],
        sections: [
          {
            title: "Staff register",
            tone: scope === "f11" ? "info" : scope === "g13" ? "success" : "primary",
            rows: scopedRows,
            columns: staffColumns,
            emptyMessage: "No staff records found for this scope.",
          },
        ],
      };
    }

    if (activeView.kind === "department") {
      const summary = visibleDepartmentSummary.find(
        (item) => item.department === activeView.name
      );
      const rows = visibleStaffDetails.filter((row) => row.department === activeView.name);

      return {
        title: activeView.name,
        subtitle: selectedBranchLabel,
        summaryBadges: [
          { label: "Staff", value: rows.length },
          { label: "F-11", value: summary?.f11 || 0 },
          { label: "G-13", value: summary?.g13 || 0 },
        ],
        sections: [
          {
            title: "Staff in this department",
            tone: "primary",
            rows,
            columns: staffColumns,
            emptyMessage: "No staff records found for this department.",
          },
        ],
      };
    }

    if (activeView.kind === "attendance-day") {
      const summary = visibleDaySummaries.find((item) => item.date === activeView.date);
      if (!summary) return null;

      return {
        title: summary.label,
        subtitle: formatDate(summary.date),
        summaryBadges: [
          { label: "Present", value: summary.present },
          { label: "Late", value: summary.late },
          { label: "Absent", value: summary.absent },
          { label: "On Leave", value: summary.onLeave },
        ],
        sections: [
          {
            title: "Present staff",
            tone: "success",
            rows: visiblePresent.filter((row) => row.date === summary.date),
            columns: presentColumns,
            emptyMessage: "No present staff found for this day.",
          },
          {
            title: "Late staff",
            tone: "warning",
            rows: visibleLate.filter((row) => row.date === summary.date),
            columns: lateColumns,
            emptyMessage: "No late staff found for this day.",
          },
          {
            title: "Absent staff",
            tone: "danger",
            rows: visibleAbsent.filter((row) => row.date === summary.date),
            columns: absentColumns,
            emptyMessage: "No absent staff found for this day.",
          },
          {
            title: "Approved leave",
            tone: "info",
            rows: visibleLeave.filter((row) => row.date === summary.date),
            columns: leaveColumns,
            emptyMessage: "No approved leave records found for this day.",
          },
        ],
      };
    }

    if (activeView.kind === "metric") {
      const isLate = activeView.type === "late";
      return {
        title: isLate ? "Late staff" : "Absent staff",
        subtitle: formatDate(selectedDateKey),
        summaryBadges: visibleDaySummaries.map((item) => ({
          label: item.label,
          value: item[activeView.type] || 0,
        })),
        sections: [
          {
            title: isLate ? "Late staff" : "Absent staff",
            tone: isLate ? "warning" : "danger",
            rows: isLate ? visibleLate : visibleAbsent,
            columns: isLate ? lateColumns : absentColumns,
            emptyMessage: isLate
              ? "No late staff found for this attendance window."
              : "No absent staff found for this attendance window.",
          },
        ],
      };
    }

    if (activeView.kind === "approvals") {
      const approvalMap = {
        leave: {
          title: "Pending leave applications",
          tone: "warning",
          rows: visiblePendingLeaves,
          columns: leaveApprovalColumns,
          emptyMessage: "No pending leave applications.",
        },
        approved: {
          title: "Approved leave applications",
          tone: "success",
          rows: visibleApprovedLeaves,
          columns: approvedLeaveColumns,
          emptyMessage: "No approved leave applications in this scope.",
        },
        duty: {
          title: "Pending duty-hour requests",
          tone: "warning",
          rows: visibleDuty,
          columns: dutyApprovalColumns,
          emptyMessage: "No pending duty-hour requests.",
        },
        document: {
          title: "Pending document reviews",
          tone: "info",
          rows: visibleDocuments,
          columns: documentApprovalColumns,
          emptyMessage: "No pending document reviews.",
        },
      };
      const target = approvalMap[activeView.type];
      if (!target) return null;

      return {
        title: target.title,
        subtitle: selectedBranchLabel,
        summaryBadges: [{ label: "Records", value: target.rows.length }],
        sections: [target],
      };
    }

    if (activeView.kind === "month-list") {
      const listMap = {
        late: {
          title: "Late employees this month",
          tone: "warning",
          rows: monthStats.latePeople,
          valueKey: "lateDays",
          valueLabel: "Late days",
          emptyMessage: "No late marks recorded this month.",
        },
        punctual: {
          title: "Punctual employees this month",
          tone: "success",
          rows: monthStats.punctualPeople,
          valueKey: "onTimeDays",
          valueLabel: "On-time days",
          emptyMessage: "No employee stayed on time for every recorded day this month.",
        },
        absent: {
          title: "Most absences this month",
          tone: "danger",
          rows: monthStats.absentPeople,
          valueKey: "count",
          valueLabel: "Absent days",
          emptyMessage: "No absence records this month.",
        },
        leave: {
          title: "Most leave this month",
          tone: "info",
          rows: monthStats.leavePeople,
          valueKey: "days",
          valueLabel: "Leave days",
          emptyMessage: "No approved leave falls in this month.",
        },
      };
      const target = listMap[activeView.type];
      if (!target) return null;
      return {
        title: target.title,
        subtitle: monthLabel,
        summaryBadges: [{ label: "People", value: target.rows.length }],
        sections: [
          {
            title: target.title,
            tone: target.tone,
            rows: target.rows,
            columns: [
              { label: "S.No", render: (_, index) => index + 1 },
              { label: "Employee", key: "name" },
              { label: "Department", key: "department" },
              { label: "Branch", key: "branch" },
              { label: target.valueLabel, key: target.valueKey },
            ],
            emptyMessage: target.emptyMessage,
          },
        ],
      };
    }

    return null;
  })();

  return (
    <div className="wrapper">
      <main className="page-content customer-contnent">
        <div className="site-main-design ad-shell">
          <MenuButton />
          <DashboardModal config={modalConfig} onClose={() => setActiveView(null)} />

          <section className="ad-topbar hr-topbar">
            <div>
              <p className="ad-eyebrow">Human resources</p>
              <h2 className="ad-title">HR Dashboard</h2>
              <p className="ad-subtitle">
                {selectedBranchLabel} · {dayLabel} · {monthLabel}
              </p>
            </div>
            <div className="ad-toolbar">
              <DashboardBranchFilter
                role={role}
                loginBranchId={branch_id}
                value={selectedBranch}
                onChange={setSelectedBranch}
                aria-label="Branch"
              />
              <input
                type="date"
                className="form-control"
                value={selectedDate}
                aria-label="Date"
                onChange={(event) => setSelectedDate(event.target.value)}
                max={moment().add(1, "year").format("YYYY-MM-DD")}
              />
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={fetchData}
                disabled={loading}
              >
                {loading ? "Loading..." : "Reload"}
              </button>
            </div>
          </section>

          {visiblePendingLeaves.length > 0 || visibleDuty.length > 0 ? (
            <div className="hr-reminders">
              {visiblePendingLeaves.length > 0 ? (
                <button
                  type="button"
                  className="hr-reminder"
                  onClick={() => setActiveView({ kind: "approvals", type: "leave" })}
                >
                  <i className="bi bi-exclamation-circle-fill"></i>
                  <span>
                    <strong>
                      {visiblePendingLeaves.length} leave{" "}
                      {visiblePendingLeaves.length === 1 ? "application" : "applications"} waiting
                    </strong>
                    <small>Open to approve or reject</small>
                  </span>
                </button>
              ) : null}
              {visibleDuty.length > 0 ? (
                <button
                  type="button"
                  className="hr-reminder hr-reminder-duty"
                  onClick={() => setActiveView({ kind: "approvals", type: "duty" })}
                >
                  <i className="bi bi-clock-history"></i>
                  <span>
                    <strong>
                      {visibleDuty.length} duty-hour{" "}
                      {visibleDuty.length === 1 ? "request" : "requests"} waiting
                    </strong>
                    <small>Open to approve or reject</small>
                  </span>
                </button>
              ) : null}
            </div>
          ) : null}

          <h3 className="ad-section-title">{dayLabel} · {selectedBranchLabel}</h3>
          {loading ? (
            <div className="ad-kpi-grid">
              <Skeleton height={96} />
              <Skeleton height={96} />
              <Skeleton height={96} />
              <Skeleton height={96} />
            </div>
          ) : (
            <div className="ad-kpi-grid">
              <Kpi
                label="Present"
                value={visibleDaySummaries[0]?.present || 0}
                sub="Recorded check-ins"
                icon="bi-person-check"
                tone="teal"
                onClick={() => setActiveView({ kind: "attendance-day", date: selectedDateKey })}
              />
              <Kpi
                label="On time"
                value={todayOnTime.length}
                sub="Present and not late"
                icon="bi-clock"
                tone="green"
                onClick={() => setActiveView({ kind: "attendance-day", date: selectedDateKey })}
              />
              <Kpi
                label="Late"
                value={visibleLate.length}
                sub="After the 15 minute grace"
                icon="bi-alarm"
                tone="amber"
                onClick={() => setActiveView({ kind: "metric", type: "late" })}
              />
              <Kpi
                label="Absent"
                value={visibleAbsent.length}
                sub="Marked absent"
                icon="bi-person-x"
                tone="red"
                onClick={() => setActiveView({ kind: "metric", type: "absent" })}
              />
              <Kpi
                label="On leave"
                value={visibleLeave.length}
                sub="Approved leave covering this date"
                icon="bi-calendar-check"
                tone="blue"
                onClick={() => setActiveView({ kind: "attendance-day", date: selectedDateKey })}
              />
              <Kpi
                label="Staff"
                value={visibleStaffSummary.total}
                sub={showBranchSplit ? `F-11 ${visibleStaffSummary.f11} · G-13 ${visibleStaffSummary.g13}` : selectedBranchLabel}
                icon="bi-people"
                tone="slate"
                onClick={() => setActiveView({ kind: "staff", scope: "all" })}
              />
            </div>
          )}

          <h3 className="ad-section-title">This month · {monthLabel}</h3>
          {loading ? (
            <Skeleton height={280} />
          ) : (
            <>
              <div className="ad-kpi-grid">
                <Kpi
                  label="Late marks"
                  value={monthStats.lateMarks}
                  sub={`${monthStats.peopleLate} employees`}
                  icon="bi-alarm"
                  tone="amber"
                  onClick={() => setActiveView({ kind: "month-list", type: "late" })}
                />
                <Kpi
                  label="On-time marks"
                  value={monthStats.onTimeMarks}
                  sub={`${monthStats.alwaysOnTime} always on time`}
                  icon="bi-check2-circle"
                  tone="green"
                  onClick={() => setActiveView({ kind: "month-list", type: "punctual" })}
                />
                <Kpi
                  label="Absent marks"
                  value={monthStats.absentMarks}
                  sub="Recorded absences only"
                  icon="bi-person-x"
                  tone="red"
                  onClick={() => setActiveView({ kind: "month-list", type: "absent" })}
                />
                <Kpi
                  label="Leave days"
                  value={monthStats.leaveDays}
                  sub={`${monthStats.leavePeople.length} employees`}
                  icon="bi-calendar2-week"
                  tone="blue"
                  onClick={() => setActiveView({ kind: "month-list", type: "leave" })}
                />
              </div>

              <div className="row g-3 mb-4">
                <div className="col-12 col-xl-7">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Recorded attendance</h5>
                      <span className="ad-chip">Present, late, and absent rows</span>
                    </div>
                    <div className="ad-chart">
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={monthStats.daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                          <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#64748b" }} />
                          <YAxis allowDecimals={false} width={36} tick={{ fontSize: 12, fill: "#64748b" }} />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="present" name="Present" fill="#303030" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="late" name="Late" fill="#d97706" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="absent" name="Absent" fill="#e8002b" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-xl-5">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Most late this month</h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setActiveView({ kind: "month-list", type: "late" })}
                      >
                        Full list
                      </button>
                    </div>
                    <div className="ad-chart">
                      {monthStats.latePeople.length ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart
                            data={monthStats.latePeople.slice(0, 8)}
                            layout="vertical"
                            margin={{ top: 8, right: 12, left: 8, bottom: 0 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: "#64748b" }} />
                            <YAxis
                              type="category"
                              dataKey="name"
                              width={110}
                              tick={{ fontSize: 12, fill: "#64748b" }}
                            />
                            <Tooltip />
                            <Bar dataKey="lateDays" name="Late days" fill="#d97706" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="ad-empty">No late marks recorded this month.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-4">
                <div className="col-12 col-lg-6">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Late on {dayLabel.toLowerCase()}</h5>
                      <span className="ad-chip">{visibleLate.length}</span>
                    </div>
                    <RankTable
                      rows={visibleLate.map((row) => ({ ...row, lateDays: row.late_time }))}
                      valueLabel="Late by"
                      valueKey="lateDays"
                      emptyMessage="Nobody is recorded late for this date."
                    />
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Punctual on {dayLabel.toLowerCase()}</h5>
                      <span className="ad-chip">{todayOnTime.length}</span>
                    </div>
                    <RankTable
                      rows={todayOnTime.map((row) => ({ ...row, onTimeDays: row.checkin_time }))}
                      valueLabel="Check in"
                      valueKey="onTimeDays"
                      emptyMessage="No on-time check-ins for this date."
                    />
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Most leave this month</h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setActiveView({ kind: "month-list", type: "leave" })}
                      >
                        Full list
                      </button>
                    </div>
                    <RankTable
                      rows={monthStats.leavePeople}
                      valueLabel="Days"
                      valueKey="days"
                      emptyMessage="No approved leave falls in this month."
                    />
                  </div>
                </div>
                <div className="col-12 col-lg-6">
                  <div className="ad-panel">
                    <div className="ad-panel-head">
                      <h5>Most absences this month</h5>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setActiveView({ kind: "month-list", type: "absent" })}
                      >
                        Full list
                      </button>
                    </div>
                    <RankTable
                      rows={monthStats.absentPeople}
                      valueLabel="Days"
                      valueKey="count"
                      emptyMessage="No absence records this month."
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <h3 className="ad-section-title">Departments</h3>

          <div className="row mb-4">
            <div className="col-12">
              <div className="card radius-10 custom-shadow">
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={4} height={72} />
                  ) : visibleDepartmentSummary.length > 0 ? (
                    <div className="row">
                      {visibleDepartmentSummary.map((department) => (
                        <div className="col-12 col-md-6 col-xl-4 mb-3" key={department.id}>
                          <div
                            className="border rounded p-3 h-100 bg-light hr-dashboard-clickable"
                            onClick={() =>
                              setActiveView({ kind: "department", name: department.department })
                            }
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setActiveView({ kind: "department", name: department.department });
                              }
                            }}
                            role="button"
                            tabIndex={0}
                          >
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <h6 className="mb-0">{department.department}</h6>
                              <span className="badge bg-dark">{department.total}</span>
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {showBranchSplit || isF11Branch(selectedBranch) ? (
                                <span className="badge text-bg-light border">
                                  F-11: {department.f11}
                                </span>
                              ) : null}
                              {showBranchSplit || isG13Branch(selectedBranch) ? (
                                <span className="badge text-bg-light border">
                                  G-13: {department.g13}
                                </span>
                              ) : null}
                            </div>
                            <div className="small text-muted">
                              {department.topDesignations.length > 0
                                ? department.topDesignations
                                    .map(([name, count]) => `${name} (${count})`)
                                    .join(", ")
                                : "No designation breakdown"}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted text-center py-3 mb-0">
                      No department summary found for the selected branch.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <h3 className="ad-section-title">Approvals</h3>

          <div className="row mb-4">
            {loading ? (
              <>
                <div className="col-12 col-md-6 col-xl-3 mb-3">
                  <Skeleton height={140} />
                </div>
                <div className="col-12 col-md-6 col-xl-3 mb-3">
                  <Skeleton height={140} />
                </div>
                <div className="col-12 col-md-6 col-xl-3 mb-3">
                  <Skeleton height={140} />
                </div>
                <div className="col-12 col-md-6 col-xl-3 mb-3">
                  <Skeleton height={140} />
                </div>
              </>
            ) : (
              <>
                <SummaryCard
                  title="Pending Leave Applications"
                  value={visiblePendingLeaves.length}
                  tone="warning"
                  columns="col-12 col-md-6 col-xl-3"
                  onClick={() => setActiveView({ kind: "approvals", type: "leave" })}
                >
                  <div className="small text-muted">Open to approve or reject</div>
                </SummaryCard>
                <SummaryCard
                  title="Approved Leave Applications"
                  value={visibleApprovedLeaves.length}
                  tone="success"
                  columns="col-12 col-md-6 col-xl-3"
                  onClick={() => setActiveView({ kind: "approvals", type: "approved" })}
                >
                  <div className="small text-muted">Open the approved list</div>
                </SummaryCard>
                <SummaryCard
                  title="Pending Duty-Hour Requests"
                  value={visibleDuty.length}
                  tone="warning"
                  columns="col-12 col-md-6 col-xl-3"
                  onClick={() => setActiveView({ kind: "approvals", type: "duty" })}
                >
                  <div className="small text-muted">Open to approve or reject</div>
                </SummaryCard>
                <SummaryCard
                  title="Pending Document Reviews"
                  value={visibleDocuments.length}
                  tone="info"
                  columns="col-12 col-md-6 col-xl-3"
                  onClick={() => setActiveView({ kind: "approvals", type: "document" })}
                >
                  <div className="small text-muted">Open to review the file</div>
                </SummaryCard>
              </>
            )}
          </div>

          <div className="row">
            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-balloon-fill text-danger me-2"></i>
                    Birthdays This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : (
                    <DataTable
                      rows={visibleBirthdays}
                      emptyMessage="No birthdays this month."
                      columns={[
                        {
                          label: "Employee",
                          render: (row) => (
                            <strong>{`${row.first_name || ""} ${row.last_name || ""}`.trim()}</strong>
                          ),
                        },
                        {
                          label: "Department",
                          render: (row) => row.department_name || row.department || "N/A",
                        },
                        {
                          label: "Date",
                          render: (row) => (
                            <span className="badge bg-danger">{row.birthday_date}</span>
                          ),
                        },
                        {
                          label: "Age",
                          render: (row) => (
                            <span className="badge bg-info">{row.age} years</span>
                          ),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6 mb-3">
              <div className="card radius-10 custom-shadow">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">
                    <i className="bi bi-calendar-heart text-primary me-2"></i>
                    Anniversaries This Month ({moment().format("MMMM")})
                  </h5>
                </div>
                <div className="card-body">
                  {loading ? (
                    <Skeleton count={5} height={60} />
                  ) : (
                    <DataTable
                      rows={visibleAnniversaries}
                      emptyMessage="No anniversaries this month."
                      columns={[
                        {
                          label: "Employee",
                          render: (row) => (
                            <strong>{`${row.first_name || ""} ${row.last_name || ""}`.trim()}</strong>
                          ),
                        },
                        {
                          label: "Department",
                          render: (row) => row.department_name || row.department || "N/A",
                        },
                        {
                          label: "Anniversary",
                          render: (row) => (
                            <span className="badge bg-primary">{row.anniversary_date}</span>
                          ),
                        },
                        {
                          label: "Years",
                          render: (row) => (
                            <span className="badge bg-success">
                              {row.years_of_service} years
                            </span>
                          ),
                        },
                      ]}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="row mt-2">
            <div className="col-12">
              <div className="card radius-10 custom-shadow mb-4">
                <div className="card-header bg-transparent">
                  <h5 className="mb-0">Registers</h5>
                  <p className="text-muted mb-0 small">
                    Full lists for the current filters. Cards above open the same records.
                  </p>
                </div>
                <div className="card-body">
                  <div className="accordion" id="hr-dashboard-details">
                    <DetailAccordionItem
                      id="staff-register"
                      title="Staff Details"
                      tone="primary"
                      count={visibleStaffDetails.length}
                    >
                      <DataTable
                        rows={visibleStaffDetails}
                        emptyMessage="No staff records found."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Employee ID", key: "uid" },
                          { label: "Branch", key: "branch" },
                          { label: "Department", key: "department" },
                          { label: "Designation", key: "designation" },
                          {
                            label: "Joining Date",
                            render: (row) => formatDate(row.joining),
                          },
                          { label: "Phone", key: "phone" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="department-register"
                      title="Department Details"
                      tone="dark"
                      count={visibleDepartmentSummary.length}
                    >
                      <DataTable
                        rows={visibleDepartmentSummary}
                        emptyMessage="No department records found."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Department", key: "department" },
                          { label: "Total Staff", key: "total" },
                          { label: "F-11", key: "f11" },
                          { label: "G-13", key: "g13" },
                          { label: "Designation Breakdown", key: "designationSummary" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="present-register"
                      title="Present Details"
                      tone="success"
                      count={visiblePresent.length}
                    >
                      <DataTable
                        rows={visiblePresent}
                        emptyMessage="No present staff found for this date."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Duty Hours",
                            render: (row) =>
                              row.duty_start_time !== "N/A"
                                ? `${row.duty_start_time} - ${row.duty_end_time}`
                                : "N/A",
                          },
                          { label: "Check In", key: "checkin_time" },
                          { label: "Check Out", key: "checkout_time" },
                          { label: "Working Hours", key: "working_hours" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                          {
                            label: "Status",
                            render: (row) => (
                              <span
                                className={`badge bg-${
                                  row.status === "Late" ? "warning" : "success"
                                }`}
                              >
                                {row.status}
                              </span>
                            ),
                          },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="late-register"
                      title="Late Details"
                      tone="warning"
                      count={visibleLate.length}
                    >
                      <DataTable
                        rows={visibleLate}
                        emptyMessage="No late staff found for this date."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Duty Hours",
                            render: (row) =>
                              row.duty_start_time !== "N/A"
                                ? `${row.duty_start_time} - ${row.duty_end_time}`
                                : "N/A",
                          },
                          { label: "Check In", key: "checkin_time" },
                          {
                            label: "Late By",
                            render: (row) => (
                              <span className="badge bg-warning">{row.late_time}</span>
                            ),
                          },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="absent-register"
                      title="Absent Details"
                      tone="danger"
                      count={visibleAbsent.length}
                    >
                      <DataTable
                        rows={visibleAbsent}
                        emptyMessage="No absent staff found for this date."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          { label: "Reason", key: "reason" },
                          { label: "Remarks", key: "remarks" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="leave-register"
                      title="Leave Details"
                      tone="info"
                      count={visibleLeave.length}
                    >
                      <DataTable
                        rows={visibleLeave}
                        emptyMessage="No approved leave found for this date."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          { label: "Employee", key: "name" },
                          { label: "Date", render: (row) => formatDate(row.date) },
                          {
                            label: "Leave From",
                            render: (row) => formatDate(row.leave_from_date),
                          },
                          {
                            label: "Leave To",
                            render: (row) => formatDate(row.leave_to_date),
                          },
                          { label: "Leave Type", key: "leave_type" },
                          { label: "Reason", key: "reason" },
                          { label: "Branch", key: "branch" },
                          { label: "Designation", key: "designation" },
                          { label: "Department", key: "department" },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="leave-approvals-register"
                      title="Leave Application Requests"
                      tone="warning"
                      count={visiblePendingLeaves.length}
                    >
                      <DataTable
                        rows={visiblePendingLeaves}
                        emptyMessage="No pending leave applications."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          {
                            label: "Employee",
                            render: (row) => row.user_info?.name || "N/A",
                          },
                          {
                            label: "Branch",
                            render: (row) =>
                              row.branch_info?.name ||
                              getBranchLabel(row.branch_id),
                          },
                          {
                            label: "Type",
                            render: (row) => row.leave_status || "N/A",
                          },
                          {
                            label: "Leave Type",
                            render: (row) => row.leave_type || "N/A",
                          },
                          {
                            label: "Category",
                            render: (row) => row.category || "N/A",
                          },
                          {
                            label: "From",
                            render: (row) => formatDate(row.from || row.leave_from_date),
                          },
                          {
                            label: "To",
                            render: (row) => formatDate(row.to || row.leave_to_date),
                          },
                          {
                            label: "Days",
                            render: (row) => row.number_of_leaves || "N/A",
                          },
                          {
                            label: "Reason",
                            render: (row) => row.reason || "N/A",
                          },
                          {
                            label: "Actions",
                            render: (row) => {
                              const noteKey = `leave-${row.id}`;
                              return (
                                <ApprovalActions
                                  note={reviewNotes[noteKey] || ""}
                                  onNoteChange={(event) =>
                                    setReviewNotes((previous) => ({
                                      ...previous,
                                      [noteKey]: event.target.value,
                                    }))
                                  }
                                  onApprove={() =>
                                    handleReview("leave", row.id, "Approved", row)
                                  }
                                  onReject={() =>
                                    handleReview("leave", row.id, "Rejected", row)
                                  }
                                  approveLoading={actionKey === `review-${noteKey}-Approved`}
                                  rejectLoading={actionKey === `review-${noteKey}-Rejected`}
                                />
                              );
                            },
                          },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="duty-approvals-register"
                      title="Duty-Hour Change Requests"
                      tone="warning"
                      count={visibleDuty.length}
                    >
                      <DataTable
                        rows={visibleDuty}
                        emptyMessage="No pending duty-hour change requests."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          {
                            label: "Employee",
                            render: (row) => row.employee?.name || "N/A",
                          },
                          {
                            label: "Employee ID",
                            render: (row) => row.employee?.uid || "N/A",
                          },
                          {
                            label: "Branch",
                            render: (row) =>
                              row.branch?.name ||
                              getBranchLabel(row.branch_id, row.branch_name),
                          },
                          { label: "Day", key: "day" },
                          {
                            label: "Current Hours",
                            render: (row) =>
                              `${row.current_start_time || "--"} - ${row.current_end_time || "--"}`,
                          },
                          {
                            label: "Requested Hours",
                            render: (row) =>
                              `${row.requested_start_time || "--"} - ${row.requested_end_time || "--"}`,
                          },
                          {
                            label: "Reason",
                            render: (row) => row.reason || "N/A",
                          },
                          {
                            label: "Actions",
                            render: (row) => {
                              const noteKey = `duty-${row.id}`;
                              return (
                                <ApprovalActions
                                  note={reviewNotes[noteKey] || ""}
                                  onNoteChange={(event) =>
                                    setReviewNotes((previous) => ({
                                      ...previous,
                                      [noteKey]: event.target.value,
                                    }))
                                  }
                                  onApprove={() => handleReview("duty", row.id, "Approved")}
                                  onReject={() => handleReview("duty", row.id, "Rejected")}
                                  approveLoading={actionKey === `review-${noteKey}-Approved`}
                                  rejectLoading={actionKey === `review-${noteKey}-Rejected`}
                                />
                              );
                            },
                          },
                        ]}
                      />
                    </DetailAccordionItem>

                    <DetailAccordionItem
                      id="document-approvals-register"
                      title="Document Approval Requests"
                      tone="primary"
                      count={visibleDocuments.length}
                    >
                      <DataTable
                        rows={visibleDocuments}
                        emptyMessage="No pending document reviews."
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          {
                            label: "Employee",
                            render: (row) => row.employee?.name || "N/A",
                          },
                          {
                            label: "Employee ID",
                            render: (row) => row.employee?.uid || "N/A",
                          },
                          {
                            label: "Branch",
                            render: (row) =>
                              row.branch || row.branch?.name || getBranchLabel(row.branch_id),
                          },
                          {
                            label: "Document",
                            render: (row) => row.subject || row.document_category || "Document",
                          },
                          { label: "Category", key: "document_category" },
                          {
                            label: "Issue Date",
                            render: (row) => formatDate(row.issue_date),
                          },
                          {
                            label: "File",
                            render: (row) =>
                              row.file_url ? (
                                <a href={row.file_url} target="_blank" rel="noreferrer">
                                  View File
                                </a>
                              ) : (
                                "N/A"
                              ),
                          },
                          {
                            label: "Description",
                            render: (row) => row.description || "N/A",
                          },
                          {
                            label: "Actions",
                            render: (row) => {
                              const noteKey = `document-${row.id}`;
                              return (
                                <ApprovalActions
                                  note={reviewNotes[noteKey] || ""}
                                  onNoteChange={(event) =>
                                    setReviewNotes((previous) => ({
                                      ...previous,
                                      [noteKey]: event.target.value,
                                    }))
                                  }
                                  onApprove={() => handleReview("document", row.id, "Approved")}
                                  onReject={() => handleReview("document", row.id, "Rejected")}
                                  approveLoading={actionKey === `review-${noteKey}-Approved`}
                                  rejectLoading={actionKey === `review-${noteKey}-Rejected`}
                                />
                              );
                            },
                          },
                        ]}
                      />
                    </DetailAccordionItem>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HRDashboard;
